export interface TrackPoint {
  coordinates: { lat: number; lon: number };
  timestamp: string;
  altitude: number;
  ground_altitude?: number;
  agl?: number;
}

export interface ReplayPoint extends TrackPoint {
  elapsedSeconds: number;
  groundAltitude: number;
  circling: boolean;
}

export interface CameraPose {
  lon: number;
  lat: number;
  altitude: number;
  bearing: number;
}

export function buildReplayTrack(track: TrackPoint[]): ReplayPoint[] {
  const firstTimestamp = Date.parse(track[0]?.timestamp);
  let elapsed = 0;
  const points = track.map((point, index) => {
    const recorded = (Date.parse(point.timestamp) - firstTimestamp) / 1000;
    elapsed = index === 0 ? 0 : Number.isFinite(recorded) && recorded > elapsed
      ? recorded : elapsed + 1;
    return {
      ...point,
      elapsedSeconds: elapsed,
      circling: false,
      groundAltitude: (point.ground_altitude ?? 0) > 0
        ? point.ground_altitude!
        : Math.max(point.altitude - Math.max(point.agl || 0, 0), 0),
    };
  });
  // Prefix totals and moving window endpoints keep track preparation linear.
  const distances = [0];
  const turns = [0];
  let previousHeading: number | undefined;
  for (let i = 1; i < points.length; i++) {
    const north = (points[i].coordinates.lat - points[i - 1].coordinates.lat) * 111195;
    const east = (points[i].coordinates.lon - points[i - 1].coordinates.lon) * 111195
      * Math.cos(points[i].coordinates.lat * Math.PI / 180);
    const distance = Math.hypot(north, east);
    distances[i] = distances[i - 1] + distance;
    turns[i] = turns[i - 1];
    // Ignore stationary GPS noise when accumulating signed turns.
    if (distance >= 1) {
      const heading = Math.atan2(east, north) * 180 / Math.PI;
      if (previousHeading !== undefined) {
        turns[i] += ((heading - previousHeading + 540) % 360 + 360) % 360 - 180;
      }
      previousHeading = heading;
    }
  }
  let left = 0;
  let right = 0;
  for (const point of points) {
    while (points[left].elapsedSeconds < point.elapsedSeconds - 30) left++;
    while (right + 1 < points.length && points[right + 1].elapsedSeconds <= point.elapsedSeconds + 30) right++;
    const north = (points[right].coordinates.lat - points[left].coordinates.lat) * 111195;
    const east = (points[right].coordinates.lon - points[left].coordinates.lon) * 111195
      * Math.cos(point.coordinates.lat * Math.PI / 180);
    const distance = distances[right] - distances[left];
    const efficiency = distance > 0 ? Math.hypot(north, east) / distance : 0;
    point.circling = distance > 30 && efficiency < 0.65
      && Math.abs(turns[right] - turns[left]) > 150;
  }
  return points;
}

// Binary search keeps frame work bounded even for day-long tracks.
export function sampleReplay(track: ReplayPoint[], progress: number) {
  if (!track.length) return null;
  if (track.length === 1) return { point: track[0], segment: 0 };
  const seconds = Math.min(Math.max(progress, 0), 1) * track[track.length - 1].elapsedSeconds;
  let low = 1;
  let high = track.length - 1;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (track[mid].elapsedSeconds < seconds) low = mid + 1;
    else high = mid;
  }
  const a = track[low - 1];
  const b = track[low];
  const ratio = (seconds - a.elapsedSeconds) / (b.elapsedSeconds - a.elapsedSeconds);
  return {
    segment: low - 1,
    point: {
      ...a,
      coordinates: {
        lat: a.coordinates.lat + (b.coordinates.lat - a.coordinates.lat) * ratio,
        lon: a.coordinates.lon + (b.coordinates.lon - a.coordinates.lon) * ratio,
      },
      altitude: a.altitude + (b.altitude - a.altitude) * ratio,
      groundAltitude: a.groundAltitude + (b.groundAltitude - a.groundAltitude) * ratio,
    },
  };
}

export function followCamera(current: CameraPose, target: CameraPose, seconds: number): CameraPose {
  const movement = 1 - Math.exp(-Math.max(seconds, 0) / 0.25);
  const turn = 1 - Math.exp(-Math.max(seconds, 0) / 1.8);
  const bearingDelta = ((target.bearing - current.bearing + 540) % 360 + 360) % 360 - 180;
  const maxTurn = Math.max(seconds, 0) * 6;
  return {
    lon: current.lon + (target.lon - current.lon) * movement,
    lat: current.lat + (target.lat - current.lat) * movement,
    altitude: current.altitude + (target.altitude - current.altitude) * movement,
    bearing: current.bearing + Math.min(Math.max(bearingDelta * turn, -maxTurn), maxTurn),
  };
}

export function replayBearing(track: ReplayPoint[], progress: number, fallback: number) {
  const duration = track[track.length - 1]?.elapsedSeconds || 1;
  if (sampleReplay(track, progress)?.point.circling) return fallback;
  const window = Math.min(30, duration * 0.4) / duration;
  const before = sampleReplay(track, progress - window)?.point;
  const after = sampleReplay(track, progress + window)?.point;
  if (!before || !after) return fallback;
  const north = after.coordinates.lat - before.coordinates.lat;
  const east = (after.coordinates.lon - before.coordinates.lon) * Math.cos(before.coordinates.lat * Math.PI / 180);
  if (Math.hypot(north, east) < 0.0001) return fallback;
  const bearing = Math.atan2(east, north) * 180 / Math.PI;
  const change = ((bearing - fallback + 540) % 360 + 360) % 360 - 180;
  return Math.abs(change) < 10 ? fallback : bearing;
}
